import type { AppUpdateKind, AppVersionInfo } from "./types";

export type FetchVersionOptions = {
  signal?: AbortSignal;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const s = value.trim();
  return s.length ? s : undefined;
}

export async function fetchRemoteVersionInfo(
  options: FetchVersionOptions = {}
): Promise<AppVersionInfo> {
  // Cache-bust and also request no-store to avoid CDN/browser stale reads.
  const url = `/version.json?cb=${Date.now()}`;
  const res = await fetch(url, {
    method: "GET",
    cache: "no-store",
    headers: {
      "Cache-Control": "no-store"
    },
    signal: options.signal
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch version.json (${res.status})`);
  }

  const data: unknown = await res.json();
  if (!isRecord(data)) {
    throw new Error("Invalid version.json payload");
  }

  const buildId = getString(data.buildId);
  const builtAt = getString(data.builtAt);

  if (!buildId || !builtAt) {
    throw new Error("Invalid version.json payload (missing buildId/builtAt)");
  }

  return {
    buildId,
    builtAt,
    vercelEnv: getString(data.vercelEnv) ?? null,
    gitCommitSha: getString(data.gitCommitSha) ?? null,
    deploymentId: getString(data.deploymentId) ?? null,
    breaking: data.breaking === true,
    minBuiltAt: getString(data.minBuiltAt) ?? null
  };
}

const timeOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
};

/** What the deployed build means for the one running. */
export const updateKindOf = (
  local: { buildId: string; builtAt: string },
  remote: AppVersionInfo
): AppUpdateKind => {
  if (!remote.buildId || remote.buildId === local.buildId) return "none";
  if (remote.breaking) return "forced";
  const floor = timeOf(remote.minBuiltAt);
  const built = timeOf(local.builtAt);
  if (floor !== null && built !== null && built < floor) return "forced";
  return "soft";
};
